import { CalculatorOutlined, EditOutlined } from '@ant-design/icons';
import { TextField } from '@refinedev/antd';
import { Button, Flex, Form, Skeleton, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

import {
  ModalFormActions,
  ShowInfoGrid,
  ShowInfoItem,
} from '@/components/general/ShowInfo/ShowInfo';
import { ShowSection } from '@/components/general/ShowInfo/ShowSection';
import { useCountryContext } from '@/contexts/CountryContext';
import { useUserContext } from '@/contexts/UserContext';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { SERVICE_ORDER_STATUS, ServiceOrder } from '@/model/ServiceOrder/ServiceOrder';

import ServiceOrderAdminData from '../ServiceOrderForm/ServiceOrderAdminData';
import { formatWithSuffix } from './serviceOrderShowHelpers';
import useInlineSectionEdit from './useInlineSectionEdit';

interface ServiceOrderShowAdminProps {
  serviceOrder: ServiceOrder;
  refetch?: () => Promise<any>;
  modifingAdminData?: boolean;
  setModifingAdminData?: (modifying: boolean) => void;
  blockEdit: boolean;
}

const ServiceOrderShowAdmin = (props: ServiceOrderShowAdminProps) => {
  const { userWorkArea } = useUserContext();
  const { isBrasilSelected } = useCountryContext();
  const { t } = useTranslation();
  const {
    serviceOrder,
    refetch,
    modifingAdminData = false,
    setModifingAdminData,
    blockEdit,
  } = props;
  const { kilometres_to_pay, kilometres_rate, hourly_rate, hours_to_pay, currency } = serviceOrder;
  const { formProps, isSaving, startEditing, handleFinish } = useInlineSectionEdit({
    id: serviceOrder.id,
    record: serviceOrder,
    isEditing: modifingAdminData,
    setIsEditing: setModifingAdminData ?? (() => undefined),
    refetch: refetch ?? (() => Promise.resolve()),
    successMessage: t('Orden de servicio actualizada correctamente'),
  });
  // Botón Editar visible solo cuando no estamos editando y el estado/permisos lo permiten.
  const canEdit =
    !blockEdit &&
    !modifingAdminData &&
    !isSaving &&
    serviceOrder.status != SERVICE_ORDER_STATUS.FINALIZADA &&
    (userWorkArea === USER_WORK_AREA.ADMINISTRATION || userWorkArea === USER_WORK_AREA.SOFTWARE);
  const editButton = canEdit ? (
    <Button size="small" icon={<EditOutlined />} onClick={startEditing}>
      {t('Editar')}
    </Button>
  ) : undefined;

  return (
    <ShowSection
      title={t('Detalles de administración')}
      className="ServiceOrderShowAdmin"
      headerExtra={editButton}
      icon={<CalculatorOutlined />}>
      {modifingAdminData ? (
        <div className="ServiceOrderShowAdminEditMode">
          <Form
            {...formProps}
            initialValues={serviceOrder}
            onFinish={handleFinish}
            layout="vertical">
            <ServiceOrderAdminData
              formProps={formProps}
              hideTechnicianReported={isBrasilSelected}
            />
            <ModalFormActions onCancel={() => setModifingAdminData?.(false)} />
          </Form>
        </div>
      ) : (
        <>
          <ShowInfoGrid>
            {!isBrasilSelected && (
              <>
                <ShowInfoItem label={t('Horas informadas por técnico')}>
                  <TextField value={formatWithSuffix(serviceOrder.hours, 'hs')} />
                </ShowInfoItem>
                <ShowInfoItem label={t('Kilómetros informados por técnico')}>
                  <TextField value={formatWithSuffix(serviceOrder.kilometres, 'km')} />
                </ShowInfoItem>
              </>
            )}
            <ShowInfoItem label={t('Horas a pagar')}>
              <TextField value={formatWithSuffix(hours_to_pay, 'hs')} />
            </ShowInfoItem>
            <ShowInfoItem label={t('Kilómetros a pagar')}>
              <TextField value={formatWithSuffix(kilometres_to_pay, 'km')} />
            </ShowInfoItem>
          </ShowInfoGrid>

          {!isSaving && (
            <>
              <div className="ServiceOrderShowAdminTotal">
                <b>{`${t('TOTAL POR HORAS Y KILÓMETROS')} (${t('sin IVA')})`}</b>
                <span className="ServiceOrderShowAdminTotalValue">
                  {kilometres_to_pay !== undefined &&
                  kilometres_rate !== undefined &&
                  hours_to_pay !== undefined &&
                  hourly_rate !== undefined &&
                  currency !== undefined
                    ? formatWithSuffix(
                        kilometres_to_pay * kilometres_rate + hours_to_pay * hourly_rate,
                        currency?.simbolo,
                      )
                    : '-'}
                </span>
              </div>
              <ShowInfoItem label={t('Números de factura')}>
                <Flex gap={4} wrap>
                  {serviceOrder.billing_numbers
                    ?.split(',')
                    .filter((n: string) => n.trim())
                    .map((number: string) => <Tag key={number.trim()}>{number.trim()}</Tag>) || '-'}
                </Flex>
              </ShowInfoItem>
            </>
          )}
          {isSaving && <Skeleton active />}
        </>
      )}
    </ShowSection>
  );
};

export default ServiceOrderShowAdmin;
